import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Field, Modal } from '../../../../components';
import type { MailboxRecord, MailboxUsage } from '../../../../mock-data/mailbox-records';
import { Body, Line, DeleteButton } from './DeleteMailboxDialog.styles';

export type DeleteMailboxDialogProps = {
  open: boolean;
  /** The mailbox up for deletion. Kept while the dialog animates closed, so the copy never flickers. */
  mailbox: MailboxRecord | null;
  /** Its usage, for the message count; `null` when there is none to read (the copy then names none). */
  usage: MailboxUsage | null;
  onCancel: () => void;
  /** Confirmed: the caller dispatches `remove` and says what happened. */
  onConfirm: (email: string) => void;
};

/** The word that unlocks Delete. Exact and case-sensitive: the capitals are the point. */
export const DELETE_CONFIRMATION = 'DELETE';

/**
 * Deleting a (paid) mailbox, from the mailbox page's trash: ONE dialog, so the copy and the friction
 * live in one place (no tile offers Delete).
 *
 * Scenario C ALWAYS requires `DELETE` typed in capitals, whatever the message count. The house
 * pattern elsewhere scales the friction (a never-used mailbox gets a plain confirm, a used one
 * requires the local part typed); not here, for two reasons: this delete destroys a paid mailbox
 * with no refund, which that framing does not cover, and friction that appears only sometimes trains
 * operators to click through the version that does not.
 *
 * The shared Modal owns Escape, the backdrop, focus return and the keep-mounted exit; nothing of
 * that is reimplemented here. The gate lives in the handler as well as on the button.
 */
export const DeleteMailboxDialog = ({
  open,
  mailbox,
  usage,
  onCancel,
  onConfirm,
}: DeleteMailboxDialogProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const [confirmText, setConfirmText] = React.useState('');
  // Cleared on every open, so a previous attempt never pre-fills the next.
  React.useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);
  // Trimmed because people paste; otherwise exact.
  const unlocked = confirmText.trim() === DELETE_CONFIRMATION;
  const address = mailbox ? mailbox.email : '';
  const onProceed = React.useCallback(() => {
    if (!mailbox || !unlocked) return;
    onConfirm(mailbox.email);
  }, [mailbox, unlocked, onConfirm]);

  return (
    <Modal
      open={open}
      title={t('Delete {{address}}?', { address })}
      onClose={onCancel}
      footer={
        <>
          <Button label={t('Cancel')} variant="outline" onClick={onCancel} />
          <DeleteButton label={t('Delete mailbox')} variant="primary" onClick={onProceed} disabled={!unlocked} />
        </>
      }
    >
      <Body data-testid="mailbox-delete-dialog">
        {mailbox && usage && usage.messagesUsed > 0 ? (
          <Line
            text="This permanently destroys the mailbox and the {{n}} messages in it. It cannot be undone. The address becomes available again."
            placeholders={{ n: usage.messagesUsed.toLocaleString(i18n.language) }}
            tag="span"
          />
        ) : (
          <Line
            text="This permanently destroys the mailbox and everything in it. It cannot be undone. The address becomes available again."
            tag="span"
          />
        )}
        {mailbox?.billing === 'paid' ? <Line text="What it cost is not refunded." tag="span" /> : null}
        <Field
          label={t('Type DELETE to confirm')}
          value={confirmText}
          onChange={setConfirmText}
          placeholder={DELETE_CONFIRMATION}
        />
      </Body>
    </Modal>
  );
};

export default DeleteMailboxDialog;
